import { UserRoles } from '../App';

// Feature IDs matching those in AdminFeatures
export const FeatureIds = {
  UNLIMITED_LIKES: 'unlimited_likes',
  SEE_WHO_LIKES: 'see_who_likes',
  SUPER_LIKES: 'super_likes',
  REWIND: 'rewind',
  INCOGNITO: 'incognito',
  MESSAGE_BEFORE_MATCH: 'message_before_match',
  BASIC_MATCHING: 'basic_matching',
  BASIC_MESSAGING: 'basic_messaging'
};

// Store features configuration
let featuresConfig: any[] = [];

// Store feature usage counts (in memory for demo)
const featureUsage: { [key: string]: { count: number; lastReset: Date } } = {};

// Initialize features with configuration
export function initializeFeatures(features: any[]): void {
  featuresConfig = features;
}

// Check if user has access to a feature
export function hasFeatureAccess(featureId: string, userRole: string): boolean {
  const feature = featuresConfig.find(f => f.id === featureId);
  if (!feature) return false;
  
  // If feature is disabled, no one has access
  if (!feature.isEnabled) return false;
  
  // If feature is not premium, everyone has access
  if (!feature.isPremium) return true;
  
  // Premium features are available to premium and admin users
  return userRole === UserRoles.PREMIUM || userRole === UserRoles.ADMIN;
}

// Record feature usage (for tracking limits)
export function recordFeatureUsage(featureId: string): void {
  const today = new Date().toDateString();
  
  if (!featureUsage[featureId]) {
    featureUsage[featureId] = { count: 1, lastReset: new Date() };
  } else {
    const lastResetDate = new Date(featureUsage[featureId].lastReset).toDateString();
    if (lastResetDate !== today) {
      // Reset count for new day
      featureUsage[featureId] = { count: 1, lastReset: new Date() };
    } else {
      featureUsage[featureId].count++;
    }
  }
}

// Get remaining uses for a feature
export function getRemainingUses(featureId: string, userRole: string): number {
  const feature = featuresConfig.find(f => f.id === featureId);
  if (!feature) return 0;
  
  // Premium and admin users have unlimited uses
  if (userRole === UserRoles.PREMIUM || userRole === UserRoles.ADMIN) {
    return -1; // -1 indicates unlimited
  }
  
  // If no limit set, return unlimited
  if (!feature.limit || feature.limit === 0) {
    return -1;
  }
  
  // Calculate remaining uses for free users
  const usage = featureUsage[featureId];
  if (!usage) {
    return feature.limit;
  }
  
  const today = new Date().toDateString();
  const lastResetDate = new Date(usage.lastReset).toDateString();
  
  if (lastResetDate !== today) {
    // Reset for new day
    return feature.limit;
  }
  
  return Math.max(0, feature.limit - usage.count);
}

// Alias for backward compatibility
export const getRemainingUsage = getRemainingUses;